import React, { useState, useEffect, useCallback } from "react";
import { 
  getDevisById, 
  getSectionTypes, 
  getSousSections, 
  getItems,
  getSousItems,
  updateDevis
} from "./api";
import { CaretRight, CaretDown } from "@phosphor-icons/react";
import "./EditDevis.css";

function EditDevis({ devisId, onBack }) {
  const [devis, setDevis] = useState(null);
  const [sectionTypes, setSectionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  
  // États pour les données chargées
  const [sousSections, setSousSections] = useState({});
  const [items, setItems] = useState({});
  const [sousItems, setSousItems] = useState({});

  // États pour l'expansion/collapse (non utilisés pour le moment)
  // const [expandedSections, setExpandedSections] = useState(new Set());
  // const [expandedSousSections, setExpandedSousSections] = useState(new Set());
  // const [expandedItems, setExpandedItems] = useState(new Set());

  // États pour l'interface de sélection avec cases à cocher
  const [addExpandedSections, setAddExpandedSections] = useState(new Set());
  const [addExpandedSousSections, setAddExpandedSousSections] = useState(new Set());
  const [addExpandedItems, setAddExpandedItems] = useState(new Set());

  // États pour les remarques et unités
  const [remarques, setRemarques] = useState({
    sections: {},
    sousSections: {},
    items: {},
    sousItems: {},
    global: ""
  });

  const [unites, setUnites] = useState({});
  const [sousSectionValues, setSousSectionValues] = useState({});
  const [priceData, setPriceData] = useState({});

  const loadAdditionalData = useCallback(async (elements, token) => {
    // Charger les sous-sections, items et sous-items nécessaires
    const sectionIds = [...new Set(elements.filter(e => e.type === 'section').map(e => e.data.id))];
    
    for (const sectionId of sectionIds) {
      try {
        const [sousSectionsData, itemsData] = await Promise.all([
          getSousSections(token, sectionId),
          getItems(token, sectionId)
        ]);
        
        setSousSections(prev => ({ ...prev, [sectionId]: sousSectionsData }));
        setItems(prev => ({ ...prev, [sectionId]: itemsData }));
        
        // Charger les sous-items pour chaque item
        for (const item of itemsData) {
          try {
            const sousItemsData = await getSousItems(token, item.id);
            setSousItems(prev => ({ ...prev, [item.id]: sousItemsData }));
          } catch (error) {
            console.error("Erreur lors du chargement des sous-items:", error);
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement des données supplémentaires:", error);
      }
    }
  }, []);

  const loadDevisData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const [devisData, sectionTypesData] = await Promise.all([
        getDevisById(token, devisId),
        getSectionTypes(token)
      ]);

      // Hydrater les états de remarques/valeurs à partir des éléments existants
      const initialRemarques = { sections: {}, sousSections: {}, items: {}, sousItems: {}, global: "" };
      const initialUnites = {};
      const initialSousSectionValues = {};
      const initialPriceData = {};

      if (Array.isArray(devisData.elements)) {
        devisData.elements.forEach((el) => {
          if (!el || !el.type || !el.data) return;
          const id = el.data.id;
          if (el.remarque) {
            if (el.type === 'section') initialRemarques.sections[id] = el.remarque;
            if (el.type === 'sousSection') initialRemarques.sousSections[id] = el.remarque;
            if (el.type === 'item') initialRemarques.items[id] = el.remarque;
            if (el.type === 'sousItem') initialRemarques.sousItems[id] = el.remarque;
          }
          // Valeurs spécifiques
          if (el.type === 'sousItem' && (el.unite || el.unite === 0)) {
            initialUnites[id] = el.unite;
          }
          if (el.type === 'sousSection' && (el.value || el.value === 0)) {
            initialSousSectionValues[id] = el.value;
          }
          if (el.type === 'sousSection' && el.priceData) {
            initialPriceData[id] = {
              qt: typeof el.priceData.qt === 'number' ? el.priceData.qt : parseFloat(el.priceData.qt) || 0,
              pu: typeof el.priceData.pu === 'number' ? el.priceData.pu : parseFloat(el.priceData.pu) || 0
            };
          }
        });
      }

      setDevis(devisData);
      setRemarques(initialRemarques);
      setUnites(initialUnites);
      setSousSectionValues(initialSousSectionValues);
      setPriceData(initialPriceData);
      setSectionTypes(sectionTypesData);
      
      // Charger les données supplémentaires si le devis a des éléments
      if (devisData.elements && Array.isArray(devisData.elements)) {
        await loadAdditionalData(devisData.elements, token);
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Erreur lors du chargement:", error);
      setMessage("Erreur lors du chargement du devis");
      setLoading(false);
    }
  }, [devisId, loadAdditionalData]);

  useEffect(() => {
    loadDevisData();
  }, [loadDevisData]);


  const handleSaveChanges = async () => {
    try {
      const token = localStorage.getItem("token");
      // Re-projeter les remarques/valeurs dans les éléments
      const updatedElements = (devis.elements || []).map((el) => {
        if (!el || !el.type || !el.data) return el;
        const id = el.data.id;
        const copy = { ...el };
        if (el.type === 'section') copy.remarque = remarques.sections[id] || '';
        if (el.type === 'sousSection') {
          copy.remarque = remarques.sousSections[id] || '';
          // valeurs pour sections spéciales
          if (sousSectionValues[id] !== undefined) {
            copy.value = sousSectionValues[id];
          }
          if (priceData[id]) {
            const qt = parseFloat(priceData[id].qt) || 0;
            const pu = parseFloat(priceData[id].pu) || 0;
            copy.priceData = { qt, pu };
          }
        }
        if (el.type === 'item') copy.remarque = remarques.items[id] || '';
        if (el.type === 'sousItem') {
          copy.remarque = remarques.sousItems[id] || '';
          if (unites[id] !== undefined) copy.unite = unites[id];
        }
        return copy;
      });

      const updatedDevis = { 
        ...devis, 
        elements: updatedElements,
        // Ajouter les données séparées comme dans CreateDevis
        remarques: remarques,
        unites: unites,
        priceData: priceData,
        sousSectionValues: sousSectionValues
      };

      await updateDevis(token, devisId, updatedDevis);
      setMessage("Devis modifié avec succès");
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      setMessage("Erreur lors de la sauvegarde");
    }
  };

  const handleRemarqueChange = (type, id, value) => {
    setRemarques(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [id]: value
      }
    }));
  };

  const handleUniteChange = (sousItemId, value) => {
    setUnites(prev => ({
      ...prev,
      [sousItemId]: value
    }));
  };

  const handleSousSectionValueChange = (sousSectionId, value) => {
    setSousSectionValues(prev => ({
      ...prev,
      [sousSectionId]: value
    }));
  };

  const handlePriceInputChange = (elementId, field, value) => {
    setPriceData(prev => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  // Fonctions pour l'interface de sélection avec cases à cocher
  const normalize = (text) =>
    (text || "")
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[-']/g, ' ')
      .toLowerCase();

  const getOrderIndex = (description) => {
    const d = normalize(description);
    const desiredOrder = [
      'etendue des travaux',
      'controle qualite hse',
      'bordereau de prix',
      'delai de realisation',
      'conditions de paiement',
      "validite de l offre"
    ];
    if (d.includes('qualite') && d.includes('hse')) return 1;
    for (let i = 0; i < desiredOrder.length; i += 1) {
      if (d.includes(desiredOrder[i])) return i;
    }
    return desiredOrder.length + 1;
  };

  // Fonctions de vérification d'existence dans le devis
  const isSectionInDevis = (sectionId) => {
    return devis.elements && devis.elements.some(e => e.type === 'section' && e.data?.id === sectionId);
  };

  const isSousSectionInDevis = (sousSectionId) => {
    return devis.elements && devis.elements.some(e => e.type === 'sousSection' && e.data?.id === sousSectionId);
  };

  const isItemInDevis = (itemId) => {
    return devis.elements && devis.elements.some(e => e.type === 'item' && e.data?.id === itemId);
  };

  const isSousItemInDevis = (sousItemId) => {
    return devis.elements && devis.elements.some(e => e.type === 'sousItem' && e.data?.id === sousItemId);
  };

  // Fonctions de gestion des cases à cocher
  const handleAddSectionToggle = (section) => {
    const isSelected = isSectionInDevis(section.id);
    
    if (isSelected) {
      // Retirer la section et tous ses enfants
      setDevis(prev => ({
        ...prev,
        elements: prev.elements.filter(e => 
          !(e.type === 'section' && e.data?.id === section.id) &&
          !(e.parentSection && e.parentSection.id === section.id)
        )
      }));
    } else {
      // Ajouter la section
      const newElement = {
        type: 'section',
        data: section,
        parentSection: null
      };
      setDevis(prev => ({
        ...prev,
        elements: [...(prev.elements || []), newElement]
      }));
    }
  };

  const handleAddSousSectionToggle = (sousSection, parentSection) => {
    const isSelected = isSousSectionInDevis(sousSection.id);
    
    if (isSelected) {
      // Retirer la sous-section et tous ses enfants
      setDevis(prev => ({
        ...prev,
        elements: prev.elements.filter(e => 
          !(e.type === 'sousSection' && e.data?.id === sousSection.id) &&
          !(e.parentSousSection && e.parentSousSection.id === sousSection.id)
        )
      }));
    } else {
      // Ajouter la sous-section
      const newElement = {
        type: 'sousSection',
        data: sousSection,
        parentSection: parentSection
      };
      setDevis(prev => ({
        ...prev,
        elements: [...(prev.elements || []), newElement]
      }));
    }
  };

  const handleAddItemToggle = (item, parentSousSection, parentSection) => {
    const isSelected = isItemInDevis(item.id);
    
    if (isSelected) {
      // Retirer l'item et tous ses enfants
      setDevis(prev => ({
        ...prev,
        elements: prev.elements.filter(e => 
          !(e.type === 'item' && e.data?.id === item.id) &&
          !(e.parentItem && e.parentItem.id === item.id)
        )
      }));
    } else {
      // Ajouter l'item
      const newElement = {
        type: 'item',
        data: item,
        parentSection: parentSection,
        parentSousSection: parentSousSection
      };
      setDevis(prev => ({
        ...prev,
        elements: [...(prev.elements || []), newElement]
      }));
    }
  };

  const handleAddSousItemToggle = (sousItem, parentItem, parentSousSection, parentSection) => {
    const isSelected = isSousItemInDevis(sousItem.id);
    
    if (isSelected) {
      // Retirer le sous-item et tous ses enfants
      setDevis(prev => ({
        ...prev,
        elements: prev.elements.filter(e => 
          !(e.type === 'sousItem' && e.data?.id === sousItem.id) &&
          !(e.parentSousItem && e.parentSousItem.id === sousItem.id)
        )
      }));
    } else {
      // Ajouter le sous-item
      const newElement = {
        type: 'sousItem',
        data: sousItem,
        parentItem: parentItem,
        parentSousSection: parentSousSection,
        parentSection: parentSection
      };
      setDevis(prev => ({
        ...prev,
        elements: [...(prev.elements || []), newElement]
      }));
    }
  };

  // Fonctions d'expansion/collapse pour l'interface d'ajout
  const handleAddSectionExpand = (section) => {
    setAddExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section.id)) {
        newSet.delete(section.id);
      } else {
        newSet.add(section.id);
        // Charger les sous-sections si pas encore fait
        if (!sousSections[section.id]) {
          loadSousSectionsForSection(section.id);
        }
      }
      return newSet;
    });
  };

  const handleAddSousSectionExpand = (sousSection, parentSection) => {
    setAddExpandedSousSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sousSection.id)) {
        newSet.delete(sousSection.id);
      } else {
        newSet.add(sousSection.id);
        // Charger les items si pas encore fait
        if (!items[sousSection.id]) {
          loadItemsForSousSection(sousSection.id);
        }
      }
      return newSet;
    });
  };

  const handleAddItemExpand = (item) => {
    setAddExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(item.id)) {
        newSet.delete(item.id);
      } else {
        newSet.add(item.id);
        // Charger les sous-items si pas encore fait
        if (!sousItems[item.id]) {
          loadSousItemsForItem(item.id);
        }
      }
      return newSet;
    });
  };

  // Fonctions de chargement des données
  const loadSousSectionsForSection = async (sectionId) => {
    try {
      const token = localStorage.getItem("token");
      const sousSectionsData = await getSousSections(token, sectionId);
      setSousSections(prev => ({ ...prev, [sectionId]: sousSectionsData }));
    } catch (error) {
      console.error("Erreur lors du chargement des sous-sections:", error);
    }
  };

  const loadItemsForSousSection = async (sousSectionId) => {
    try {
      const token = localStorage.getItem("token");
      const itemsData = await getItems(token, sousSectionId);
      setItems(prev => ({ ...prev, [sousSectionId]: itemsData }));
    } catch (error) {
      console.error("Erreur lors du chargement des items:", error);
    }
  };

  const loadSousItemsForItem = async (itemId) => {
    try {
      const token = localStorage.getItem("token");
      const sousItemsData = await getSousItems(token, itemId);
      setSousItems(prev => ({ ...prev, [itemId]: sousItemsData }));
    } catch (error) {
      console.error("Erreur lors du chargement des sous-items:", error);
    }
  };

  // Fonction pour rendre un élément (non utilisée pour le moment)
  /*
  const renderElement = (element) => {
    const sectionType = element.parentSection?.description || element.data?.description || "";
    
    // Qualité/HSE
    if (sectionType.includes('Qualité') || sectionType.includes('HSE')) {
      return (
        <div key={element.id || Math.random()} className="section-element quality-hse">
          {element.type === 'section' && (
            <div className="section-item selected">
              <div className="section-item-header">
                <div className="section-title">
                  <h4>{element.data.description}</h4>
                </div>
              </div>
              <div className="section-remarque">
                <textarea
                  placeholder="Ajouter une remarque..."
                  value={remarques.sections[element.data.id] || ""}
                  onChange={(e) => handleRemarqueChange('sections', element.data.id, e.target.value)}
                  className="remarque-input"
                />
              </div>
            </div>
          )}
          {element.type === 'sousSection' && (
            <div className="sous-section-item selected">
              <div className="sous-section-header">
                <div className="sous-section-title">
                  <span className="bullet">•</span>{element.data.description}
                </div>
              </div>
              <div className="sous-section-remarque">
                <textarea
                  placeholder="Ajouter une remarque..."
                  value={remarques.sousSections[element.data.id] || ""}
                  onChange={(e) => handleRemarqueChange('sousSections', element.data.id, e.target.value)}
                  className="remarque-input"
                />
              </div>
            </div>
          )}
          {element.type === 'item' && (
            <div className="item-item selected">
              <div className="item-header">
                <div className="item-title">
                  <span className="bullet">-</span>{element.data.description}
                </div>
              </div>
              <div className="item-remarque">
                <textarea
                  placeholder="Ajouter une remarque..."
                  value={remarques.items[element.data.id] || ""}
                  onChange={(e) => handleRemarqueChange('items', element.data.id, e.target.value)}
                  className="remarque-input"
                />
              </div>
            </div>
          )}
          {element.type === 'sousItem' && (
            <div className="sous-item-item selected">
              <div className="sous-item-header">
                <div className="sous-item-title">
                  <span className="indent">--</span>{element.data.description}
                </div>
                <div className="sous-item-unite">
                  <input
                    type="text"
                    placeholder="Unité (ex: 380)"
                    value={unites[element.data.id] || ""}
                    onChange={(e) => handleUniteChange(element.data.id, e.target.value)}
                    className="unite-input"
                  />
                  <span className="unite"> µm</span>
                </div>
              </div>
              <div className="sous-item-remarque">
                <textarea
                  placeholder="Ajouter une remarque..."
                  value={remarques.sousItems[element.data.id] || ""}
                  onChange={(e) => handleRemarqueChange('sousItems', element.data.id, e.target.value)}
                  className="remarque-input"
                />
              </div>
            </div>
          )}
        </div>
      );
    }

    // Sections spéciales
    if (sectionType.includes('Délai de réalisation') || sectionType.includes('Conditions de paiement') || sectionType.includes("Validité de l'offre")) {
      return (
        <div key={element.id || Math.random()} className="section-element special-section">
          {element.type === 'section' && (
            <div className="section-item selected">
              <div className="section-item-header">
                <div className="section-title">
                  <h4>{element.data.description}</h4>
                </div>
              </div>
              <div className="section-remarque">
                <textarea
                  placeholder="Ajouter une remarque..."
                  value={remarques.sections[element.data.id] || ""}
                  onChange={(e) => handleRemarqueChange('sections', element.data.id, e.target.value)}
                  className="remarque-input"
                />
              </div>
            </div>
          )}
          {element.type === 'sousSection' && (
            <div className="sous-section-item selected">
              <div className="sous-section-header">
                <div className="sous-section-title-with-value">
                  <span className="bullet">•</span>
                  <span className="sous-section-text">{element.data.description}</span>
                  <div className="sous-section-value-input">
                    <input
                      type="text"
                      placeholder="Valeur..."
                      value={sousSectionValues[element.data.id] || ""}
                      onChange={(e) => handleSousSectionValueChange(element.data.id, e.target.value)}
                      className="value-input"
                    />
                  </div>
                </div>
              </div>
              <div className="sous-section-remarque">
                <textarea
                  placeholder="Ajouter une remarque..."
                  value={remarques.sousSections[element.data.id] || ""}
                  onChange={(e) => handleRemarqueChange('sousSections', element.data.id, e.target.value)}
                  className="remarque-input"
                />
              </div>
            </div>
          )}
        </div>
      );
    }

    // Par défaut
    return (
      <div key={element.id || Math.random()} className="section-element work-scope">
        {element.type === 'section' && (
          <div className="section-item selected">
            <div className="section-item-header">
              <div className="section-title">
                <h4>{element.data.description}</h4>
              </div>
            </div>
            <div className="section-remarque">
              <textarea
                placeholder="Ajouter une remarque..."
                value={remarques.sections[element.data.id] || ""}
                onChange={(e) => handleRemarqueChange('sections', element.data.id, e.target.value)}
                className="remarque-input"
              />
            </div>
          </div>
        )}
        {element.type === 'sousSection' && (
          <div className="sous-section-item selected">
            <div className="sous-section-header">
              <div className="sous-section-title">
                <span className="arrow">➤</span>
                {element.data.description}
              </div>
            </div>
            <div className="sous-section-remarque">
              <textarea
                placeholder="Ajouter une remarque..."
                value={remarques.sousSections[element.data.id] || ""}
                onChange={(e) => handleRemarqueChange('sousSections', element.data.id, e.target.value)}
                className="remarque-input"
              />
            </div>
          </div>
        )}
        {element.type === 'item' && (
          <div className="item-item selected">
            <div className="item-header">
              <div className="item-title">
                <span className="bullet">●</span> {element.data.description}
              </div>
            </div>
            <div className="item-remarque">
              <textarea
                placeholder="Ajouter une remarque..."
                value={remarques.items[element.data.id] || ""}
                onChange={(e) => handleRemarqueChange('items', element.data.id, e.target.value)}
                className="remarque-input"
              />
            </div>
          </div>
        )}
        {element.type === 'sousItem' && (
          <div className="sous-item-item selected">
            <div className="sous-item-header">
              <div className="sous-item-title">
                <span className="indent">-</span> {element.data.description}
              </div>
              <div className="sous-item-unite">
                <input
                  type="text"
                  placeholder="Unité (ex: 380)"
                  value={unites[element.data.id] || ""}
                  onChange={(e) => handleUniteChange(element.data.id, e.target.value)}
                  className="unite-input"
                />
                <span className="unite"> µm</span>
              </div>
            </div>
            <div className="sous-item-remarque">
              <textarea
                placeholder="Ajouter une remarque..."
                value={remarques.sousItems[element.data.id] || ""}
                onChange={(e) => handleRemarqueChange('sousItems', element.data.id, e.target.value)}
                className="remarque-input"
              />
            </div>
          </div>
        )}
      </div>
    );
  };
  */

  if (loading) {
    return (
      <div className="edit-devis-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Chargement du devis...</p>
        </div>
      </div>
    );
  }

  if (!devis) {
    return (
      <div className="edit-devis-container">
        <p>Devis non trouvé</p>
        <button onClick={onBack}>Retour</button>
      </div>
    );
  }

  return (
    <div className="edit-devis-container">
      <div className="edit-header">
        <h2>Modifier le devis {devis.reference}</h2>
        <div className="header-actions">
          <button onClick={handleSaveChanges} className="save-btn">
            💾 Sauvegarder
          </button>
          <button onClick={onBack} className="back-btn">
            ← Retour
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('succès') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {/* Informations du devis */}
      <div className="devis-info">
        <h3>Informations du devis</h3>
        <div className="info-grid">
          <div className="info-item">
            <label>Référence:</label>
            <span>{devis.reference}</span>
          </div>
          <div className="info-item">
            <label>Date:</label>
            <span>{new Date(devis.date).toLocaleDateString()}</span>
          </div>
          <div className="info-item">
            <label>Client:</label>
            <span>Mr.{devis.contact || devis.societe}</span>
          </div>
          <div className="info-item">
            <label>Objet:</label>
            <span>{devis.objet}</span>
          </div>
        </div>
      </div>

      {/* Sélection des sections avec cases à cocher */}
      <div className="devis-elements">
        <h3>Sélectionner les sections</h3>
        <div className="sections-list">
          {sectionTypes
            .slice()
            .sort((a,b)=>{
              const ai = getOrderIndex(a.description); 
              const bi = getOrderIndex(b.description);
              if (ai !== bi) return ai - bi; 
              return normalize(a.description).localeCompare(normalize(b.description));
            })
            .map((section) => {
            const isSelected = isSectionInDevis(section.id);
            const isExpanded = addExpandedSections.has(section.id);
            return (
              <div key={section.id} className="section-item">
                <div className="section-item-header">
                  <div className="section-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleAddSectionToggle(section)}
                    />
                  </div>
                  <div className="section-title"><h4>{section.description}</h4></div>
                  <div className="section-arrow" onClick={() => handleAddSectionExpand(section)} title="Développer/Réduire">
                    {isExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
                  </div>
                </div>

                {isSelected && (
                  <div className="section-remarque">
                    <textarea
                      placeholder="Ajouter une remarque pour cette section..."
                      value={remarques.sections[section.id] || ""}
                      onChange={(e) => handleRemarqueChange('sections', section.id, e.target.value)}
                      className="remarque-input"
                    />
                  </div>
                )}

                {isExpanded && (
                  <div className="sous-sections-container">
                    {sousSections[section.id]?.map((sousSection) => {
                      const isSousSelected = isSousSectionInDevis(sousSection.id);
                      const isPriceSection = section.description.includes('Bordereau de prix') || section.description.includes('Prix');
                      const isSpecialSection = section.description.includes('Délai de réalisation') || section.description.includes('Conditions de paiement') || section.description.includes("Validité de l'offre");
                      return (
                        <div key={sousSection.id} className="sous-section-item">
                          <div className="sous-section-header">
                            <div className="sous-section-checkbox">
                              <input
                                type="checkbox"
                                checked={isSousSelected}
                                onChange={() => handleAddSousSectionToggle(sousSection, section)}
                              />
                            </div>
                            <div className="sous-section-title">{sousSection.description}</div>
                            <div className="sous-section-arrow" onClick={() => handleAddSousSectionExpand(sousSection, section)} title="Développer/Réduire">
                              {addExpandedSousSections.has(sousSection.id) ? <CaretDown size={14} /> : <CaretRight size={14} />}
                            </div>
                          </div>
                          
                          {isSousSelected && (
                            <>
                              {isSpecialSection && (
                                <div className="sous-section-value-input">
                                  <input
                                    type="text"
                                    placeholder="Saisir la valeur..."
                                    value={sousSectionValues[sousSection.id] || ''}
                                    onChange={(e) => handleSousSectionValueChange(sousSection.id, e.target.value)}
                                  />
                                </div>
                              )}
                              <div className="sous-section-remarque">
                                <textarea
                                  placeholder="Ajouter une remarque pour cette sous-section..."
                                  value={remarques.sousSections[sousSection.id] || ""}
                                  onChange={(e) => handleRemarqueChange('sousSections', sousSection.id, e.target.value)}
                                  className="remarque-input"
                                />
                              </div>
                            </>
                          )}
                          
                          {/* Tableau de prix pour les sections de prix */}
                          {isPriceSection && isSousSelected && (
                            <div className="price-table-container">
                              <table className="price-table">
                                <thead>
                                  <tr>
                                    <th>Désignation</th>
                                    <th>Unité</th>
                                    <th>QT</th>
                                    <th>PU (CHF) HT</th>
                                    <th>PT (CHF) HT</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td>{sousSection.description}</td>
                                    <td>{sousSection.unite || 'M²'}</td>
                                    <td>
                                      <input 
                                        type="number" 
                                        className="price-input" 
                                        placeholder="0.00"
                                        value={priceData[sousSection.id]?.qt || ''}
                                        onChange={(e) => handlePriceInputChange(sousSection.id, 'qt', e.target.value)}
                                      />
                                    </td>
                                    <td>
                                      <input 
                                        type="number" 
                                        className="price-input" 
                                        placeholder="0.00"
                                        value={priceData[sousSection.id]?.pu || ''}
                                        onChange={(e) => handlePriceInputChange(sousSection.id, 'pu', e.target.value)}
                                      />
                                    </td>
                                    <td className="price-total">
                                      {(() => {
                                        const qt = priceData[sousSection.id]?.qt || 0;
                                        const pu = priceData[sousSection.id]?.pu || 0;
                                        return (qt * pu).toFixed(2);
                                      })()}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          )}
                          
                          {isSousSelected && !isPriceSection && !isSpecialSection && (
                            <div className="items-container">
                              {(items[sousSection.id] || []).map((item) => {
                                const isItemSelected = isItemInDevis(item.id);
                                return (
                                  <div key={item.id} className="item-item">
                                    <div className="item-header">
                                      <div className="item-checkbox">
                                        <input
                                          type="checkbox"
                                          checked={isItemSelected}
                                          onChange={() => handleAddItemToggle(item, sousSection, section)}
                                        />
                                      </div>
                                      <div className="item-title"><span className="bullet">●</span>{item.description}</div>
                                      <div className="item-arrow" onClick={() => handleAddItemExpand(item)} title="Développer/Réduire">
                                        {addExpandedItems.has(item.id) ? <CaretDown size={12} /> : <CaretRight size={12} />}
                                      </div>
                                    </div>
                                    
                                    {isItemSelected && (
                                      <div className="item-remarque">
                                        <textarea
                                          placeholder="Ajouter une remarque pour cet item..."
                                          value={remarques.items[item.id] || ""}
                                          onChange={(e) => handleRemarqueChange('items', item.id, e.target.value)}
                                          className="remarque-input"
                                        />
                                      </div>
                                    )}
                                    
                                    {/* Sous-items */}
                                    {addExpandedItems.has(item.id) && (
                                      <div className="sous-items-container">
                                        {(sousItems[item.id] || []).map((sousItem) => {
                                          const isSousItemSelected = isSousItemInDevis(sousItem.id);
                                          return (
                                            <div key={sousItem.id} className="sous-item-item">
                                              <div className="sous-item-header">
                                                <div className="sous-item-checkbox">
                                                  <input
                                                    type="checkbox"
                                                    checked={isSousItemSelected}
                                                    onChange={() => handleAddSousItemToggle(sousItem, item, sousSection, section)}
                                                  />
                                                </div>
                                                <div className="sous-item-title">
                                                  {sousItem.description}
                                                </div>
                                                <div className="sous-item-unite">
                                                  <input
                                                    type="text"
                                                    placeholder="Valeur (ex: 380)"
                                                    value={unites[sousItem.id] || ""}
                                                    onChange={(e) => handleUniteChange(sousItem.id, e.target.value)}
                                                  />
                                                </div>
                                              </div>
                                              
                                              {isSousItemSelected && (
                                                <div className="sous-item-remarque">
                                                  <textarea
                                                    placeholder="Ajouter une remarque pour ce sous-item..."
                                                    value={remarques.sousItems[sousItem.id] || ""}
                                                    onChange={(e) => handleRemarqueChange('sousItems', sousItem.id, e.target.value)}
                                                    className="remarque-input"
                                                  />
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Résumé des éléments sélectionnés */}
      <div className="devis-elements">
        <h3>Éléments sélectionnés</h3>
        {devis.elements && Array.isArray(devis.elements) && devis.elements.length > 0 ? (
          <div className="elements-summary">
            <p><strong>Sections:</strong> {(() => {
              const sections = devis.elements.filter(e => e.type === 'section').map(e => e.data?.description).filter(Boolean);
              return sections.length > 0 ? sections.join(', ') : 'Aucune';
            })()}</p>
            
            <p><strong>Sous-sections:</strong> {(() => {
              const sousSections = devis.elements.filter(e => e.type === 'sousSection').map(e => e.data?.description).filter(Boolean);
              return sousSections.length > 0 ? sousSections.join(', ') : 'Aucune';
            })()}</p>
            
            <p><strong>Items:</strong> {(() => {
              const items = devis.elements.filter(e => e.type === 'item').map(e => e.data?.description).filter(Boolean);
              return items.length > 0 ? items.join(', ') : 'Aucune';
            })()}</p>
          </div>
        ) : (
          <div className="no-elements">
            <p>Aucun élément sélectionné dans ce devis.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default EditDevis; 